import { FC, ReactElement } from 'react';

import { IPaymentSetupRequestResponse, useFeatureFlag, useMultiplePaymentSetupQuery } from 'obt-common';
import { getBookingPaymentDetails } from 'obt-common/utils/payment/getBookingPaymentDetails';
import { getPaymentSetupRequests } from 'obt-common/utils/payment/getPaymentSetupRequests';

import PreloadErrorModal from 'src/v2-components/trips/shared/PreloadErrorModal';
import { useFormContext } from 'rhf-v7';
import Loader from 'src/app/flights/ExchangeFlightCheckoutPage/shared/Loader';
import { RetryPaymentCheckoutFormValues } from '../FormProvider/types';
import useGetTravelerProfileResponse from '../hooks/useGetTravelerProfileResponse';
import { useListBookingPaymentSourcesResponse } from '../hooks/useListBookingPaymentSourcesResponse';
import useGetPnr from '../hooks/useGetPnr';

interface Props {
  children: (props: { allPaymentSetupResponse: IPaymentSetupRequestResponse[] }) => ReactElement;
}

export const PreloadContainerFormDependent: FC<Props> = ({ children }) => {
  const isPaymentSetupCallEnabled = useFeatureFlag('FE_PAYMENTS_SETUP_CALL_RETRY_PAYMENTS');
  const { watch } = useFormContext<RetryPaymentCheckoutFormValues>();
  const paymentSourceIds = watch('selectedPaymentSourceIds');

  const traveler = useGetTravelerProfileResponse();

  const pnr = useGetPnr();
  const pnrId = pnr?.pnrId || undefined;

  const listBookingPaymentSourcesResponse = useListBookingPaymentSourcesResponse();

  const bookingPaymentDetails = getBookingPaymentDetails({
    paymentSourceIds,
    numSelectedPoints: undefined,
    listBookingPaymentSourcesResponse,
  });

  const paymentSetupRequestInfos = getPaymentSetupRequests({
    bookingPaymentDetails,
    primaryTraveler: traveler || null,
    listBookingPaymentSourcesResponse: listBookingPaymentSourcesResponse || null,
    paymentSetupId: { pnrId: pnrId ?? '' },
  });

  const paymentSetupQueryOptions = (paymentSetupRequestInfos || []).map((requestInfo) => ({
    requestInfo,
    queryOptions: { enabled: !!isPaymentSetupCallEnabled },
  }));

  const allPaymentSetup = useMultiplePaymentSetupQuery(paymentSetupQueryOptions);

  const allPaymentSetupResponse = allPaymentSetup
    .map((s) => s.data)
    .filter((s): s is IPaymentSetupRequestResponse => !!s);

  const allQueries = [...allPaymentSetup];

  const firstQueryWithError = allQueries.find((query) => query.error);

  if (firstQueryWithError?.error && isPaymentSetupCallEnabled) {
    return <PreloadErrorModal error={firstQueryWithError.error} refetch={firstQueryWithError.refetch} />;
  }

  if (allPaymentSetupResponse) {
    return children({
      allPaymentSetupResponse,
    });
  }

  return <Loader />;
};
